<template>
    <BaseField
        :label="label"
        :hint="hint"
        :is-optional="isOptional"
        :name="name"
        :error-messages="errorMessagesTotal"
        :data-spec-class="dataSpecClass"
        :custom-theme-base="customThemeBase"
        :is-inline="isInline"
    >
        <div :class="['relative rounded-md', wrapperClassAddition]">
            <input
                v-bind="$attrs"
                ref="input"
                :type="type"
                :name="name"
                :class="classObject"
                :placeholder="placeholder"
                :disabled="isDisabled"
                :value="modelValue"
                :data-spec-class="`${dataSpecClass}__input`"
                @input="$emit('update:modelValue', $event.target.value)"
                @blur="hasAutoDirty ? vuelidateField?.$touch() : () => {}"
            />

            <div
                v-if="errorMessagesTotal.length || !!$slots.addOn || isClearable"
                class="absolute inset-y-0 right-0 flex items-center pr-3"
            >
                <ExclamationCircleIcon
                    v-if="errorMessagesTotal.length"
                    class="h-5 w-5 text-danger"
                    aria-hidden="true"
                />

                <span
                    v-if="isClearable && modelValue"
                    data-spec-class="field-input__clear"
                    @click="
                        $emit('update:modelValue', '');
                        $emit('clear');
                    "
                >
                    <XCircleIcon class="h-5 w-5 cursor-pointer text-tertiary hover:text-muted" />
                </span>

                <span
                    v-if="!!$slots.addOn"
                    class="ml-1 text-tertiary sm:text-sm"
                >
                    <slot name="addOn" />
                </span>
            </div>
        </div>
    </BaseField>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { ExclamationCircleIcon, XCircleIcon } from '@heroicons/vue/20/solid';
import { FormTheme, TextFieldType, Theme } from '@/types';
import BaseField from './BaseField.vue';
import { useField } from '@/composables/field';

const { normalThemeFieldClasses, errorThemeFieldClasses, disabledThemeFieldClasses } = useField();

defineEmits(['update:modelValue', 'clear']);

const input = ref(null);

function focus() {
    input.value.focus();
}

function select() {
    input.value.select();
}

defineExpose({ focus, select });

interface Props {
    name: string;
    label?: string;
    hint?: string;
    placeholder?: string;
    isOptional?: boolean;
    isDisabled?: boolean;
    isClearable?: boolean;
    type?: TextFieldType;
    modelValue?: string;
    vuelidateField?: unknown;
    errorMessages?: string[];
    wrapperClassAddition?: string;
    customThemeBase?: FormTheme | string;
    customThemeNormal?: FormTheme | string;
    dataSpecClass?: string;
    hasAutoDirty?: boolean;
    isInline?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    label: '',
    hint: '',
    placeholder: '',
    isOptional: false,
    isDisabled: false,
    isClearable: false,
    type: TextFieldType.Text,
    modelValue: undefined,
    vuelidateField: undefined,
    errorMessages: () => [],
    wrapperClassAddition: undefined,
    customThemeBase: undefined,
    customThemeNormal: undefined,
    dataSpecClass: 'field-input',
    hasAutoDirty: true,
    isInline: false,
});

const vuelidateErrors = computed(() => {
    if (props.vuelidateField?.$errors.length) {
        return props.vuelidateField.$errors.map((error) => error.$message);
    }

    return [];
});

const errorMessagesTotal = computed(() => [...vuelidateErrors.value, ...props.errorMessages]);

const theme = computed(() => {
    if (props.isDisabled) {
        return Theme.Disabled;
    }
    if (errorMessagesTotal.value.length) {
        return Theme.Danger;
    }

    return Theme.Normal;
});

const themeBase = computed(() => {
    if (props.customThemeBase === FormTheme.Website) {
        return 'w-full rounded-sm p-4 text-dark';
    }
    if (typeof props.customThemeBase === 'string') {
        return props.customThemeBase;
    }

    return 'block w-full rounded-md shadow-xs sm:text-sm';
});

const themeNormal = computed(() => {
    if (typeof props.customThemeNormal === 'string') {
        return props.customThemeNormal;
    }
    return [normalThemeFieldClasses];
});

const classObject = computed(() => ({
    'transition-ease': true,
    [themeBase.value]: true,
    [themeNormal.value]: theme.value === Theme.Normal,
    [errorThemeFieldClasses]: theme.value === Theme.Danger,
    [disabledThemeFieldClasses]: theme.value === Theme.Disabled,
}));
</script>

<script lang="ts">
export default {
    inheritAttrs: false,
};
</script>
